// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================

import React, { useState } from 'react';
import { useAuth } from '../AuthContext';

const FIELDS_OF_INVENTION = [
  'Software / AI',
  'Hardware / Electronics',
  'Mechanical',
  'Chemical / Biotech',
  'Agriculture',
  'Energy',
  'Healthcare',
  'Transportation',
  'Construction',
  'Manufacturing',
  'Other',
];

const STEP_LABELS = [
  'Invention Details',
  'Problem & Solution',
  'Technical Details',
  'Claims',
  'Figures',
  'Review & Generate',
];

const ENTITY_FEES = {
  micro: { label: 'Micro Entity', fee: 200 },
  small: { label: 'Small Entity', fee: 320 },
  regular: { label: 'Regular', fee: 640 },
};

const DEMO_DRAFTS = [
  {
    id: 'demo-1',
    title: 'Automated Soil Moisture Monitoring System with Predictive Irrigation',
    field: 'Agriculture',
    status: 'Draft',
    date: '2026-02-08',
  },
  {
    id: 'demo-2',
    title: 'AI-Powered Inventory Reconciliation Method for Multi-Location Retail',
    field: 'Software / AI',
    status: 'Filed',
    date: '2026-01-22',
  },
];

function generateClaimText(data) {
  const title = data.inventionTitle || 'the invention';
  const field = data.fieldOfInvention || 'a technical field';
  const solution = data.yourSolution || 'a novel approach';
  const components = data.keyComponents || 'one or more components';
  const novel = data.novelElements || 'novel elements';
  return (
    `1. A system for ${title.toLowerCase()} in the field of ${field.toLowerCase()}, comprising:\n` +
    `   ${components.split('\n').filter(Boolean).map((c, i) => `(${String.fromCharCode(97 + i)}) ${c.trim()};`).join('\n   ')}\n` +
    `   wherein ${novel.split('\n').filter(Boolean)[0] || 'the system provides a novel improvement over existing solutions'}.\n\n` +
    `2. The system of claim 1, wherein ${solution.split('.')[0] || 'the solution addresses the identified problem'}.`
  );
}

function generatePatentDocument(data) {
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const entityInfo = ENTITY_FEES[data.filingEntity] || ENTITY_FEES.micro;

  const claimsBlock = (data.claims || []).map((c) => {
    const prefix = c.dependsOn ? `${c.number}. The system of claim ${c.dependsOn}, wherein ` : `${c.number}. `;
    return prefix + (c.text || '').trim();
  }).join('\n\n');

  const figuresBlock = (data.figures || []).filter(f => f.description).map((f, i) =>
    `FIG. ${i + 1}: ${f.description}`
  ).join('\n\n');

  return {
    title: data.inventionTitle || 'Untitled Invention',
    filingDate: today,
    inventorName: data.inventorName || 'Unknown Inventor',
    inventorAddress: data.inventorAddress || '',
    entityType: entityInfo.label,
    fee: entityInfo.fee,
    field: data.fieldOfInvention || '',
    abstract:
      `A ${(data.fieldOfInvention || 'technical').toLowerCase()} invention titled "${data.inventionTitle || 'Untitled'}". ` +
      `${data.briefDescription || ''} ` +
      `The invention addresses the problem of ${(data.problemStatement || '').split('.')[0] || 'an identified limitation'} ` +
      `by providing ${(data.yourSolution || '').split('.')[0] || 'a novel solution'}.`,
    background:
      `FIELD OF THE INVENTION\n\n` +
      `The present invention relates generally to the field of ${(data.fieldOfInvention || 'technology').toLowerCase()}, ` +
      `and more particularly to ${data.inventionTitle || 'a novel system and method'}.\n\n` +
      `DESCRIPTION OF RELATED ART\n\n` +
      `${data.problemStatement || 'There exists a need for improvement in this field.'}\n\n` +
      `Current solutions include: ${data.priorArt || 'Various approaches have been attempted but remain inadequate.'}\n\n` +
      `These existing solutions suffer from limitations that the present invention seeks to overcome.`,
    summary:
      `The present invention provides ${data.yourSolution || 'a novel approach to solving the identified problem'}.\n\n` +
      `In accordance with one embodiment, the invention comprises ${data.keyComponents || 'various components working in concert'}.\n\n` +
      `The novel aspects of the present invention include: ${data.novelElements || 'improvements over the prior art'}.`,
    detailedDescription:
      `DETAILED DESCRIPTION OF PREFERRED EMBODIMENTS\n\n` +
      `The following description provides specific details for a thorough understanding of the present invention. ` +
      `However, one skilled in the art will understand that the invention may be practiced without many of these details.\n\n` +
      `MECHANISM OF OPERATION\n\n` +
      `${data.howItWorks || 'The system operates according to the following steps.'}\n\n` +
      `KEY COMPONENTS\n\n` +
      `${data.keyComponents || 'The system comprises the following components.'}\n\n` +
      `PREFERRED EMBODIMENT\n\n` +
      `${data.preferredEmbodiment || 'In a preferred embodiment, the invention is implemented as follows.'}`,
    claims: claimsBlock || 'No claims specified.',
    figures: figuresBlock || 'No figures provided.',
  };
}

export default function PatentGeneratorPage() {
  const { user } = useAuth();

  const [currentStep, setCurrentStep] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [patentDoc, setPatentDoc] = useState(null);

  const [formData, setFormData] = useState({
    inventionTitle: '',
    fieldOfInvention: '',
    briefDescription: '',
    problemStatement: '',
    priorArt: '',
    yourSolution: '',
    howItWorks: '',
    keyComponents: '',
    novelElements: '',
    preferredEmbodiment: '',
    claims: [
      { number: 1, text: '', dependsOn: null },
    ],
    figures: [],
    inventorName: user?.full_name || user?.email || '',
    inventorAddress: '',
    filingEntity: 'micro',
  });

  const updateField = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const addClaim = (dependsOn) => {
    setFormData(prev => ({
      ...prev,
      claims: [...prev.claims, { number: prev.claims.length + 1, text: '', dependsOn: dependsOn || null }],
    }));
  };

  const updateClaim = (index, key, value) => {
    setFormData(prev => {
      const updated = [...prev.claims];
      updated[index] = { ...updated[index], [key]: value };
      return { ...prev, claims: updated };
    });
  };

  const removeClaim = (index) => {
    if (formData.claims.length <= 1) return;
    setFormData(prev => {
      const updated = prev.claims.filter((_, i) => i !== index).map((c, i) => ({ ...c, number: i + 1 }));
      return { ...prev, claims: updated };
    });
  };

  const addFigure = () => {
    if (formData.figures.length >= 12) return;
    setFormData(prev => ({
      ...prev,
      figures: [...prev.figures, { description: '', file: null }],
    }));
  };

  const updateFigure = (index, key, value) => {
    setFormData(prev => {
      const updated = [...prev.figures];
      updated[index] = { ...updated[index], [key]: value };
      return { ...prev, figures: updated };
    });
  };

  const removeFigure = (index) => {
    setFormData(prev => ({
      ...prev,
      figures: prev.figures.filter((_, i) => i !== index),
    }));
  };

  const autoGenerateClaims = () => {
    const autoText = generateClaimText(formData);
    const lines = autoText.split('\n\n').filter(Boolean);
    const newClaims = lines.map((line, i) => {
      const depMatch = line.match(/The system of claim (\d+)/);
      return {
        number: i + 1,
        text: line.replace(/^\d+\.\s*/, '').trim(),
        dependsOn: depMatch ? parseInt(depMatch[1], 10) : null,
      };
    });
    setFormData(prev => ({ ...prev, claims: newClaims.length > 0 ? newClaims : prev.claims }));
  };

  const handleGenerate = () => {
    setGenerating(true);
    setTimeout(() => {
      const doc = generatePatentDocument(formData);
      setPatentDoc(doc);
      setGenerating(false);
      setGenerated(true);
    }, 4000);
  };

  const handleDownloadHTML = () => {
    if (!patentDoc) return;
    const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Provisional Patent Application - ${patentDoc.title}</title>
<style>
  body { font-family: 'Times New Roman', Times, serif; margin: 1in; line-height: 1.8; color: #111; font-size: 12pt; }
  h1 { font-size: 16pt; text-align: center; margin-bottom: 24pt; text-transform: uppercase; }
  h2 { font-size: 13pt; text-transform: uppercase; margin-top: 20pt; border-bottom: 1px solid #333; padding-bottom: 4pt; }
  h3 { font-size: 12pt; text-transform: uppercase; margin-top: 16pt; }
  .meta { text-align: center; margin-bottom: 20pt; font-size: 11pt; color: #444; }
  .meta p { margin: 2pt 0; }
  .claims p { margin: 8pt 0; }
  .dependent { margin-left: 24pt; }
  .figures p { margin: 6pt 0; }
  @media print { body { margin: 0.75in; } }
</style>
</head>
<body>
<h1>PROVISIONAL PATENT APPLICATION</h1>
<div class="meta">
  <p><strong>Title:</strong> ${patentDoc.title}</p>
  <p><strong>Inventor:</strong> ${patentDoc.inventorName}</p>
  <p><strong>Address:</strong> ${patentDoc.inventorAddress}</p>
  <p><strong>Filing Entity:</strong> ${patentDoc.entityType}</p>
  <p><strong>Filing Fee:</strong> $${patentDoc.fee}</p>
  <p><strong>Date:</strong> ${patentDoc.filingDate}</p>
  <p><strong>Field:</strong> ${patentDoc.field}</p>
</div>
<h2>Abstract</h2>
<p>${patentDoc.abstract}</p>
<h2>Background of the Invention</h2>
${patentDoc.background.split('\n\n').map(p => '<p>' + p + '</p>').join('\n')}
<h2>Summary of the Invention</h2>
${patentDoc.summary.split('\n\n').map(p => '<p>' + p + '</p>').join('\n')}
<h2>Detailed Description</h2>
${patentDoc.detailedDescription.split('\n\n').map(p => '<p>' + p + '</p>').join('\n')}
<h2>Claims</h2>
<div class="claims">
${patentDoc.claims.split('\n\n').map(p => '<p>' + p + '</p>').join('\n')}
</div>
<h2>Figures</h2>
<div class="figures">
${patentDoc.figures.split('\n\n').map(p => '<p>' + p + '</p>').join('\n')}
</div>
</body>
</html>`;
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Patent-Draft-${(patentDoc.title || 'Untitled').replace(/\s+/g, '-').substring(0, 40)}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleEditDraft = () => {
    setGenerated(false);
    setPatentDoc(null);
    setCurrentStep(0);
  };

  const canContinue = () => {
    switch (currentStep) {
      case 0:
        return formData.inventionTitle.trim() && formData.fieldOfInvention && formData.briefDescription.trim();
      case 1:
        return formData.problemStatement.trim() && formData.yourSolution.trim();
      case 2:
        return formData.howItWorks.trim() && formData.keyComponents.trim() && formData.novelElements.trim();
      case 3:
        return formData.claims.length > 0 && formData.claims[0].text.trim();
      case 4:
        return true;
      case 5:
        return formData.inventorName.trim() && formData.inventorAddress.trim();
      default:
        return false;
    }
  };

  const entityInfo = ENTITY_FEES[formData.filingEntity] || ENTITY_FEES.micro;

  // ---- Styles ----
  const styles = {
    page: {
      padding: 24,
      maxWidth: 960,
      margin: '0 auto',
    },
    hero: {
      textAlign: 'center',
      marginBottom: 32,
      padding: '32px 16px',
      background: 'linear-gradient(135deg, rgba(99,102,241,0.12) 0%, rgba(99,102,241,0.04) 100%)',
      borderRadius: 16,
      border: '1px solid var(--border)',
    },
    heroTitle: {
      fontSize: 30,
      fontWeight: 800,
      color: 'var(--text)',
      margin: '0 0 8px',
    },
    heroSubtitle: {
      fontSize: 15,
      color: 'var(--text-muted)',
      margin: '0 0 20px',
      maxWidth: 600,
      marginLeft: 'auto',
      marginRight: 'auto',
      lineHeight: 1.5,
    },
    badgeRow: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: 10,
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '6px 14px',
      background: 'var(--card)',
      border: '1px solid var(--border)',
      borderRadius: 20,
      fontSize: 13,
      fontWeight: 600,
      color: 'var(--text)',
    },
    badgeIcon: {
      fontSize: 15,
    },
    comparisonCard: {
      marginBottom: 32,
      padding: 24,
      background: 'var(--card)',
      borderRadius: 14,
      border: '1px solid var(--border)',
    },
    comparisonTitle: {
      fontSize: 18,
      fontWeight: 700,
      color: 'var(--text)',
      margin: '0 0 16px',
      textAlign: 'center',
    },
    comparisonGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr auto 1fr',
      gap: 16,
      alignItems: 'center',
    },
    comparisonBox: {
      padding: 20,
      borderRadius: 12,
      textAlign: 'center',
    },
    comparisonTraditional: {
      background: 'rgba(239,68,68,0.08)',
      border: '1px solid rgba(239,68,68,0.2)',
    },
    comparisonHub: {
      background: 'rgba(99,102,241,0.08)',
      border: '1px solid rgba(99,102,241,0.2)',
    },
    comparisonLabel: {
      fontSize: 12,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      marginBottom: 8,
    },
    comparisonPrice: {
      fontSize: 24,
      fontWeight: 800,
      marginBottom: 6,
    },
    comparisonDetail: {
      fontSize: 13,
      color: 'var(--text-muted)',
      lineHeight: 1.4,
    },
    vsCircle: {
      width: 40,
      height: 40,
      borderRadius: '50%',
      background: 'var(--border)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 13,
      fontWeight: 700,
      color: 'var(--text-muted)',
    },
    savingsBanner: {
      textAlign: 'center',
      marginTop: 16,
      padding: '10px 16px',
      background: 'rgba(16,185,129,0.1)',
      border: '1px solid rgba(16,185,129,0.25)',
      borderRadius: 10,
      fontSize: 16,
      fontWeight: 700,
      color: '#10b981',
    },
    stepperContainer: {
      marginBottom: 28,
    },
    stepperRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0,
      flexWrap: 'wrap',
    },
    stepItem: (active, completed) => ({
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '8px 14px',
      borderRadius: 8,
      cursor: completed || active ? 'pointer' : 'default',
      background: active ? 'rgba(99,102,241,0.12)' : 'transparent',
      transition: 'background 0.15s',
    }),
    stepNumber: (active, completed) => ({
      width: 28,
      height: 28,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 13,
      fontWeight: 700,
      background: active ? '#6366f1' : completed ? '#10b981' : 'var(--border)',
      color: active || completed ? '#fff' : 'var(--text-muted)',
      flexShrink: 0,
    }),
    stepLabel: (active) => ({
      fontSize: 13,
      fontWeight: active ? 700 : 500,
      color: active ? '#6366f1' : 'var(--text-muted)',
    }),
    stepConnector: {
      width: 20,
      height: 2,
      background: 'var(--border)',
      flexShrink: 0,
    },
    formCard: {
      padding: 28,
      background: 'var(--card)',
      borderRadius: 14,
      border: '1px solid var(--border)',
      marginBottom: 24,
    },
    formTitle: {
      fontSize: 20,
      fontWeight: 700,
      color: 'var(--text)',
      margin: '0 0 4px',
    },
    formSubtitle: {
      fontSize: 13,
      color: 'var(--text-muted)',
      margin: '0 0 24px',
    },
    fieldGroup: {
      marginBottom: 20,
    },
    label: {
      display: 'block',
      fontSize: 13,
      fontWeight: 600,
      color: 'var(--text)',
      marginBottom: 6,
    },
    tipText: {
      display: 'block',
      fontSize: 12,
      color: 'var(--text-muted)',
      marginBottom: 6,
      fontStyle: 'italic',
    },
    input: {
      width: '100%',
      padding: '12px 14px',
      fontSize: 14,
      color: 'var(--text)',
      background: 'var(--bg)',
      border: '1px solid var(--border)',
      borderRadius: 8,
      outline: 'none',
      boxSizing: 'border-box',
      transition: 'border-color 0.15s',
    },
    select: {
      width: '100%',
      padding: '12px 14px',
      fontSize: 14,
      color: 'var(--text)',
      background: 'var(--bg)',
      border: '1px solid var(--border)',
      borderRadius: 8,
      outline: 'none',
      boxSizing: 'border-box',
      cursor: 'pointer',
      appearance: 'auto',
    },
    textarea: {
      width: '100%',
      padding: '12px 14px',
      fontSize: 14,
      color: 'var(--text)',
      background: 'var(--bg)',
      border: '1px solid var(--border)',
      borderRadius: 8,
      outline: 'none',
      boxSizing: 'border-box',
      minHeight: 120,
      resize: 'vertical',
      fontFamily: 'inherit',
      lineHeight: 1.5,
    },
    buttonRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 12,
      marginTop: 8,
    },
    btnPrimary: {
      padding: '10px 28px',
      fontSize: 14,
      fontWeight: 600,
      color: '#fff',
      background: '#6366f1',
      border: 'none',
      borderRadius: 8,
      cursor: 'pointer',
      transition: 'opacity 0.15s',
    },
    btnSecondary: {
      padding: '10px 28px',
      fontSize: 14,
      fontWeight: 600,
      color: 'var(--text)',
      background: 'var(--bg)',
      border: '1px solid var(--border)',
      borderRadius: 8,
      cursor: 'pointer',
      transition: 'opacity 0.15s',
    },
    btnDanger: {
      padding: '6px 14px',
      fontSize: 12,
      fontWeight: 600,
      color: '#ef4444',
      background: 'rgba(239,68,68,0.08)',
      border: '1px solid rgba(239,68,68,0.2)',
      borderRadius: 6,
      cursor: 'pointer',
    },
    btnSmall: {
      padding: '6px 14px',
      fontSize: 12,
      fontWeight: 600,
      color: '#6366f1',
      background: 'rgba(99,102,241,0.08)',
      border: '1px solid rgba(99,102,241,0.2)',
      borderRadius: 6,
      cursor: 'pointer',
    },
    btnDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    claimCard: {
      padding: 16,
      background: 'var(--bg)',
      border: '1px solid var(--border)',
      borderRadius: 10,
      marginBottom: 12,
    },
    claimHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 10,
    },
    claimNumber: {
      fontSize: 13,
      fontWeight: 700,
      color: '#6366f1',
    },
    figureCard: {
      padding: 16,
      background: 'var(--bg)',
      border: '1px solid var(--border)',
      borderRadius: 10,
      marginBottom: 12,
    },
    figureHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 10,
    },
    figureNumber: {
      fontSize: 13,
      fontWeight: 700,
      color: '#6366f1',
    },
    figureUploadZone: {
      width: '100%',
      padding: '20px 14px',
      border: '2px dashed var(--border)',
      borderRadius: 8,
      textAlign: 'center',
      fontSize: 13,
      color: 'var(--text-muted)',
      marginBottom: 10,
      cursor: 'pointer',
      background: 'transparent',
      boxSizing: 'border-box',
    },
    reviewSection: {
      marginBottom: 20,
      padding: 16,
      background: 'var(--bg)',
      borderRadius: 10,
      border: '1px solid var(--border)',
    },
    reviewLabel: {
      fontSize: 11,
      fontWeight: 700,
      color: 'var(--text-muted)',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      marginBottom: 4,
    },
    reviewValue: {
      fontSize: 14,
      color: 'var(--text)',
      lineHeight: 1.5,
      whiteSpace: 'pre-wrap',
    },
    feeCard: {
      padding: 20,
      background: 'rgba(99,102,241,0.06)',
      border: '1px solid rgba(99,102,241,0.15)',
      borderRadius: 12,
      textAlign: 'center',
      marginBottom: 20,
    },
    feeAmount: {
      fontSize: 36,
      fontWeight: 800,
      color: '#6366f1',
      margin: '8px 0 4px',
    },
    feeLabel: {
      fontSize: 13,
      color: 'var(--text-muted)',
    },
    generateBtn: {
      width: '100%',
      padding: '16px 28px',
      fontSize: 16,
      fontWeight: 700,
      color: '#fff',
      background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
      border: 'none',
      borderRadius: 10,
      cursor: 'pointer',
      transition: 'opacity 0.15s',
    },
    generatingOverlay: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 60,
      textAlign: 'center',
    },
    spinner: {
      width: 48,
      height: 48,
      border: '4px solid var(--border)',
      borderTopColor: '#6366f1',
      borderRadius: '50%',
      animation: 'patentSpin 0.8s linear infinite',
      marginBottom: 20,
    },
    previewContainer: {
      marginBottom: 32,
    },
    previewDoc: {
      background: '#ffffff',
      color: '#111111',
      padding: '48px 56px',
      borderRadius: 4,
      border: '1px solid #ccc',
      fontFamily: "'Times New Roman', Times, serif",
      lineHeight: 1.8,
      fontSize: 13,
      maxWidth: 800,
      margin: '0 auto 24px',
    },
    previewTitle: {
      fontSize: 18,
      fontWeight: 700,
      textAlign: 'center',
      textTransform: 'uppercase',
      marginBottom: 24,
      color: '#111',
      letterSpacing: 1,
    },
    previewMeta: {
      textAlign: 'center',
      marginBottom: 24,
      fontSize: 12,
      color: '#444',
      lineHeight: 1.6,
    },
    previewSectionTitle: {
      fontSize: 14,
      fontWeight: 700,
      textTransform: 'uppercase',
      marginTop: 24,
      marginBottom: 8,
      paddingBottom: 4,
      borderBottom: '1px solid #333',
      color: '#111',
    },
    previewBody: {
      whiteSpace: 'pre-wrap',
      marginBottom: 12,
      color: '#222',
    },
    actionsRow: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: 10,
      marginBottom: 32,
    },
    actionBtn: (bg, color) => ({
      padding: '10px 20px',
      fontSize: 13,
      fontWeight: 600,
      color: color,
      background: bg,
      border: 'none',
      borderRadius: 8,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
    }),
    draftsCard: {
      padding: 24,
      background: 'var(--card)',
      borderRadius: 14,
      border: '1px solid var(--border)',
      marginTop: 12,
    },
    draftsTitle: {
      fontSize: 18,
      fontWeight: 700,
      color: 'var(--text)',
      margin: '0 0 16px',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: 13,
    },
    th: {
      textAlign: 'left',
      padding: '10px 12px',
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      color: 'var(--text-muted)',
      borderBottom: '2px solid var(--border)',
    },
    td: {
      padding: '12px 12px',
      color: 'var(--text)',
      borderBottom: '1px solid var(--border)',
    },
    statusBadge: (status) => ({
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: 12,
      fontSize: 11,
      fontWeight: 600,
      background: status === 'Filed' ? 'rgba(16,185,129,0.1)' : status === 'Granted' ? 'rgba(99,102,241,0.1)' : 'rgba(245,158,11,0.1)',
      color: status === 'Filed' ? '#10b981' : status === 'Granted' ? '#6366f1' : '#f59e0b',
    }),
    entityRadioGroup: {
      display: 'flex',
      gap: 12,
      flexWrap: 'wrap',
    },
    entityRadio: (selected) => ({
      flex: '1 1 160px',
      padding: '14px 16px',
      background: selected ? 'rgba(99,102,241,0.08)' : 'var(--bg)',
      border: selected ? '2px solid #6366f1' : '1px solid var(--border)',
      borderRadius: 10,
      cursor: 'pointer',
      textAlign: 'center',
      transition: 'border-color 0.15s, background 0.15s',
    }),
    entityLabel: (selected) => ({
      fontSize: 14,
      fontWeight: selected ? 700 : 500,
      color: selected ? '#6366f1' : 'var(--text)',
      marginBottom: 2,
    }),
    entityFee: {
      fontSize: 12,
      color: 'var(--text-muted)',
    },
    responsiveGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: 0,
    },
  };

  // ---- Step Renderers ----

  const renderStep0 = () => (
    <>
      <div style={styles.fieldGroup}>
        <label style={styles.label}>Invention Title *</label>
        <span style={styles.tipText}>A concise, descriptive title for your invention (e.g., "Solar-Powered Water Purification Device with Automatic Filtration")</span>
        <input
          style={styles.input}
          type="text"
          placeholder="Enter the title of your invention"
          value={formData.inventionTitle}
          onChange={(e) => updateField('inventionTitle', e.target.value)}
        />
      </div>
      <div style={styles.fieldGroup}>
        <label style={styles.label}>Field of Invention *</label>
        <span style={styles.tipText}>Select the primary technical field your invention belongs to</span>
        <select
          style={styles.select}
          value={formData.fieldOfInvention}
          onChange={(e) => updateField('fieldOfInvention', e.target.value)}
        >
          <option value="">-- Select a field --</option>
          {FIELDS_OF_INVENTION.map((f) => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
      </div>
      <div style={styles.fieldGroup}>
        <label style={styles.label}>Brief Description *</label>
        <span style={styles.tipText}>Summarize your invention in 2-3 sentences. What does it do and who is it for?</span>
        <textarea
          style={styles.textarea}
          placeholder="Describe your invention briefly..."
          value={formData.briefDescription}
          onChange={(e) => updateField('briefDescription', e.target.value)}
        />
      </div>
    </>
  );

  const renderStep1 = () => (
    <>
      <div style={styles.fieldGroup}>
        <label style={styles.label}>Problem Statement *</label>
        <span style={styles.tipText}>What problem does this invention solve? Be specific about the pain point or limitation.</span>
        <textarea
          style={styles.textarea}
          placeholder="Describe the problem your invention addresses..."
          value={formData.problemStatement}
          onChange={(e) => updateField('problemStatement', e.target.value)}
        />
      </div>
      <div style={styles.fieldGroup}>
        <label style={styles.label}>Prior Art / Existing Solutions</label>
        <span style={styles.tipText}>What solutions currently exist? Why are they inadequate? This helps establish novelty.</span>
        <textarea
          style={styles.textarea}
          placeholder="Describe existing solutions and their limitations..."
          value={formData.priorArt}
          onChange={(e) => updateField('priorArt', e.target.value)}
        />
      </div>
      <div style={styles.fieldGroup}>
        <label style={styles.label}>Your Solution *</label>
        <span style={styles.tipText}>How does your invention solve this problem differently? Focus on what makes it unique.</span>
        <textarea
          style={styles.textarea}
          placeholder="Explain how your invention solves the problem..."
          value={formData.yourSolution}
          onChange={(e) => updateField('yourSolution', e.target.value)}
        />
      </div>
    </>
  );

  const renderStep2 = () => (
    <>
      <div style={styles.fieldGroup}>
        <label style={styles.label}>How It Works *</label>
        <span style={styles.tipText}>Describe the technical mechanism step by step. Imagine explaining it to an engineer in the field.</span>
        <textarea
          style={{ ...styles.textarea, minHeight: 150 }}
          placeholder="Step 1: The sensor detects...&#10;Step 2: Data is transmitted to...&#10;Step 3: The algorithm processes..."
          value={formData.howItWorks}
          onChange={(e) => updateField('howItWorks', e.target.value)}
        />
      </div>
      <div style={styles.fieldGroup}>
        <label style={styles.label}>Key Components *</label>
        <span style={styles.tipText}>List the main components, parts, or elements. One per line for best results.</span>
        <textarea
          style={styles.textarea}
          placeholder="a sensor module for detecting input&#10;a processing unit for analyzing data&#10;a display interface for presenting results"
          value={formData.keyComponents}
          onChange={(e) => updateField('keyComponents', e.target.value)}
        />
      </div>
      <div style={styles.fieldGroup}>
        <label style={styles.label}>Novel Elements *</label>
        <span style={styles.tipText}>What specific aspects are new and non-obvious? These form the basis of your patent claims.</span>
        <textarea
          style={styles.textarea}
          placeholder="The novel aspects include..."
          value={formData.novelElements}
          onChange={(e) => updateField('novelElements', e.target.value)}
        />
      </div>
      <div style={styles.fieldGroup}>
        <label style={styles.label}>Preferred Embodiment</label>
        <span style={styles.tipText}>Describe the best way to implement this invention. This is your recommended configuration.</span>
        <textarea
          style={styles.textarea}
          placeholder="In the preferred embodiment, the system is configured as..."
          value={formData.preferredEmbodiment}
          onChange={(e) => updateField('preferredEmbodiment', e.target.value)}
        />
      </div>
    </>
  );

  const renderStep3 = () => (
    <>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <span style={styles.tipText}>Independent claims define the broadest scope. Dependent claims add specificity and fallback protection.</span>
        </div>
        <button
          style={styles.btnSmall}
          onClick={autoGenerateClaims}
          type="button"
        >
          Auto-Generate from Technical Details
        </button>
      </div>
      {formData.claims.map((claim, i) => (
        <div key={i} style={styles.claimCard}>
          <div style={styles.claimHeader}>
            <span style={styles.claimNumber}>
              Claim {claim.number} {claim.dependsOn ? `(Dependent on Claim ${claim.dependsOn})` : '(Independent)'}
            </span>
            {formData.claims.length > 1 && (
              <button style={styles.btnDanger} onClick={() => removeClaim(i)} type="button">Remove</button>
            )}
          </div>
          {i > 0 && (
            <div style={{ marginBottom: 10 }}>
              <label style={{ ...styles.label, fontSize: 12 }}>Depends on Claim #</label>
              <select
                style={{ ...styles.select, maxWidth: 180 }}
                value={claim.dependsOn || ''}
                onChange={(e) => updateClaim(i, 'dependsOn', e.target.value ? parseInt(e.target.value, 10) : null)}
              >
                <option value="">None (Independent)</option>
                {formData.claims.filter((_, ci) => ci < i).map((c) => (
                  <option key={c.number} value={c.number}>Claim {c.number}</option>
                ))}
              </select>
            </div>
          )}
          <textarea
            style={{ ...styles.textarea, minHeight: 100 }}
            placeholder={claim.dependsOn
              ? `The system of claim ${claim.dependsOn}, wherein...`
              : 'A system/method/apparatus for..., comprising:'}
            value={claim.text}
            onChange={(e) => updateClaim(i, 'text', e.target.value)}
          />
        </div>
      ))}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button style={styles.btnSmall} onClick={() => addClaim(null)} type="button">
          + Add Independent Claim
        </button>
        <button
          style={styles.btnSmall}
          onClick={() => addClaim(formData.claims.length > 0 ? formData.claims[0].number : 1)}
          type="button"
        >
          + Add Dependent Claim
        </button>
      </div>
    </>
  );

  const renderStep4 = () => (
    <>
      <div style={{ marginBottom: 16 }}>
        <span style={styles.tipText}>
          Figures are optional for provisional patent applications but are strongly recommended.
          You can include up to 12 figures. Each figure should have a brief description.
        </span>
      </div>
      {formData.figures.map((fig, i) => (
        <div key={i} style={styles.figureCard}>
          <div style={styles.figureHeader}>
            <span style={styles.figureNumber}>FIG. {i + 1}</span>
            <button style={styles.btnDanger} onClick={() => removeFigure(i)} type="button">Remove</button>
          </div>
          <div style={styles.figureUploadZone}>
            Click to upload figure image (PNG, JPG, SVG) or drag and drop
          </div>
          <textarea
            style={{ ...styles.textarea, minHeight: 80 }}
            placeholder={`Description of FIG. ${i + 1} - e.g., "A block diagram showing the overall system architecture including sensor module, processing unit, and display interface."`}
            value={fig.description}
            onChange={(e) => updateFigure(i, 'description', e.target.value)}
          />
        </div>
      ))}
      {formData.figures.length < 12 && (
        <button style={styles.btnSmall} onClick={addFigure} type="button">
          + Add Figure {formData.figures.length > 0 ? `(${formData.figures.length}/12)` : ''}
        </button>
      )}
      {formData.figures.length === 0 && (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
          No figures added yet. Click the button above to add a figure.
        </div>
      )}
    </>
  );

  const renderStep5 = () => (
    <>
      {/* Review of all previous steps */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>Application Summary</div>
        <div style={styles.reviewSection}>
          <div style={styles.reviewLabel}>Invention Title</div>
          <div style={styles.reviewValue}>{formData.inventionTitle || '(not provided)'}</div>
        </div>
        <div style={styles.reviewSection}>
          <div style={styles.reviewLabel}>Field of Invention</div>
          <div style={styles.reviewValue}>{formData.fieldOfInvention || '(not selected)'}</div>
        </div>
        <div style={styles.reviewSection}>
          <div style={styles.reviewLabel}>Brief Description</div>
          <div style={styles.reviewValue}>{formData.briefDescription || '(not provided)'}</div>
        </div>
        <div style={styles.reviewSection}>
          <div style={styles.reviewLabel}>Problem Statement</div>
          <div style={styles.reviewValue}>{formData.problemStatement || '(not provided)'}</div>
        </div>
        <div style={styles.reviewSection}>
          <div style={styles.reviewLabel}>Prior Art / Existing Solutions</div>
          <div style={styles.reviewValue}>{formData.priorArt || '(not provided)'}</div>
        </div>
        <div style={styles.reviewSection}>
          <div style={styles.reviewLabel}>Your Solution</div>
          <div style={styles.reviewValue}>{formData.yourSolution || '(not provided)'}</div>
        </div>
        <div style={styles.reviewSection}>
          <div style={styles.reviewLabel}>How It Works</div>
          <div style={styles.reviewValue}>{formData.howItWorks || '(not provided)'}</div>
        </div>
        <div style={styles.reviewSection}>
          <div style={styles.reviewLabel}>Key Components</div>
          <div style={styles.reviewValue}>{formData.keyComponents || '(not provided)'}</div>
        </div>
        <div style={styles.reviewSection}>
          <div style={styles.reviewLabel}>Novel Elements</div>
          <div style={styles.reviewValue}>{formData.novelElements || '(not provided)'}</div>
        </div>
        <div style={styles.reviewSection}>
          <div style={styles.reviewLabel}>Preferred Embodiment</div>
          <div style={styles.reviewValue}>{formData.preferredEmbodiment || '(not provided)'}</div>
        </div>
        <div style={styles.reviewSection}>
          <div style={styles.reviewLabel}>Claims ({formData.claims.length})</div>
          <div style={styles.reviewValue}>
            {formData.claims.map((c) =>
              `Claim ${c.number}${c.dependsOn ? ` (dep. on ${c.dependsOn})` : ' (independent)'}: ${c.text.substring(0, 100)}${c.text.length > 100 ? '...' : ''}`
            ).join('\n')}
          </div>
        </div>
        <div style={styles.reviewSection}>
          <div style={styles.reviewLabel}>Figures ({formData.figures.length})</div>
          <div style={styles.reviewValue}>
            {formData.figures.length > 0
              ? formData.figures.map((f, i) => `FIG. ${i + 1}: ${f.description.substring(0, 80)}${f.description.length > 80 ? '...' : ''}`).join('\n')
              : '(none)'}
          </div>
        </div>
      </div>

      {/* Inventor Details */}
      <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>Inventor & Filing Information</div>
      <div style={styles.fieldGroup}>
        <label style={styles.label}>Inventor Name *</label>
        <input
          style={styles.input}
          type="text"
          placeholder="Full legal name of the inventor"
          value={formData.inventorName}
          onChange={(e) => updateField('inventorName', e.target.value)}
        />
      </div>
      <div style={styles.fieldGroup}>
        <label style={styles.label}>Inventor Address *</label>
        <span style={styles.tipText}>Full mailing address as it will appear on the patent application</span>
        <input
          style={styles.input}
          type="text"
          placeholder="123 Main St, City, State, ZIP, Country"
          value={formData.inventorAddress}
          onChange={(e) => updateField('inventorAddress', e.target.value)}
        />
      </div>
      <div style={styles.fieldGroup}>
        <label style={styles.label}>Filing Entity Type *</label>
        <span style={styles.tipText}>
          Micro Entity: fewer than 4 previously filed patents, income below threshold.
          Small Entity: fewer than 500 employees. Regular: all others.
        </span>
        <div style={styles.entityRadioGroup}>
          {Object.entries(ENTITY_FEES).map(([key, val]) => (
            <div
              key={key}
              style={styles.entityRadio(formData.filingEntity === key)}
              onClick={() => updateField('filingEntity', key)}
            >
              <div style={styles.entityLabel(formData.filingEntity === key)}>{val.label}</div>
              <div style={styles.entityFee}>${val.fee} filing fee</div>
            </div>
          ))}
        </div>
      </div>

      {/* Fee Calculation */}
      <div style={styles.feeCard}>
        <div style={styles.feeLabel}>Estimated Filing Fee</div>
        <div style={styles.feeAmount}>${entityInfo.fee}</div>
        <div style={styles.feeLabel}>{entityInfo.label} - USPTO Provisional Patent Application</div>
      </div>

      {/* Generate Button */}
      <button
        style={{
          ...styles.generateBtn,
          ...((!canContinue()) ? styles.btnDisabled : {}),
        }}
        disabled={!canContinue()}
        onClick={handleGenerate}
        type="button"
      >
        Generate Patent Draft
      </button>
    </>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: return renderStep0();
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      default: return null;
    }
  };

  // ---- Generating Animation ----
  const renderGenerating = () => (
    <div style={styles.generatingOverlay}>
      <style>{`@keyframes patentSpin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      <div style={styles.spinner} />
      <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
        Generating Your Patent Draft
      </div>
      <div style={{ fontSize: 14, color: 'var(--text-muted)', maxWidth: 400, lineHeight: 1.5 }}>
        Analyzing your invention details, structuring claims, and formatting in USPTO provisional application format...
      </div>
    </div>
  );

  // ---- Patent Preview ----
  const renderPatentPreview = () => {
    if (!patentDoc) return null;
    return (
      <div style={styles.previewContainer}>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)', marginBottom: 4 }}>Patent Draft Generated</div>
          <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>Review your provisional patent application below</div>
        </div>

        {/* The Document */}
        <div style={styles.previewDoc}>
          <div style={styles.previewTitle}>Provisional Patent Application</div>
          <div style={styles.previewMeta}>
            <div><strong>Title:</strong> {patentDoc.title}</div>
            <div><strong>Inventor:</strong> {patentDoc.inventorName}</div>
            <div><strong>Address:</strong> {patentDoc.inventorAddress}</div>
            <div><strong>Entity:</strong> {patentDoc.entityType} &mdash; Filing Fee: ${patentDoc.fee}</div>
            <div><strong>Date:</strong> {patentDoc.filingDate}</div>
            <div><strong>Field:</strong> {patentDoc.field}</div>
          </div>

          <div style={styles.previewSectionTitle}>Abstract</div>
          <div style={styles.previewBody}>{patentDoc.abstract}</div>

          <div style={styles.previewSectionTitle}>Background of the Invention</div>
          <div style={styles.previewBody}>{patentDoc.background}</div>

          <div style={styles.previewSectionTitle}>Summary of the Invention</div>
          <div style={styles.previewBody}>{patentDoc.summary}</div>

          <div style={styles.previewSectionTitle}>Detailed Description</div>
          <div style={styles.previewBody}>{patentDoc.detailedDescription}</div>

          <div style={styles.previewSectionTitle}>Claims</div>
          <div style={styles.previewBody}>{patentDoc.claims}</div>

          <div style={styles.previewSectionTitle}>Figures</div>
          <div style={styles.previewBody}>{patentDoc.figures}</div>
        </div>

        {/* Action Buttons */}
        <div style={styles.actionsRow}>
          <button
            style={styles.actionBtn('#6366f1', '#fff')}
            onClick={handleDownloadHTML}
            type="button"
          >
            Download Patent Draft (HTML)
          </button>
          <button
            style={styles.actionBtn('rgba(99,102,241,0.1)', '#6366f1')}
            onClick={handlePrint}
            type="button"
          >
            Print / Save PDF
          </button>
          <button
            style={styles.actionBtn('rgba(16,185,129,0.1)', '#10b981')}
            onClick={() => window.open('https://www.uspto.gov/patents/apply/patent-center', '_blank')}
            type="button"
          >
            File with USPTO
          </button>
          <button
            style={styles.actionBtn('rgba(245,158,11,0.1)', '#f59e0b')}
            onClick={() => { /* Save to Supabase placeholder */ }}
            type="button"
          >
            Save to Account
          </button>
          <button
            style={styles.actionBtn('var(--bg)', 'var(--text)')}
            onClick={handleEditDraft}
            type="button"
          >
            Edit Draft
          </button>
        </div>
      </div>
    );
  };

  // ---- Main Render ----
  return (
    <div className="page-body" style={styles.page}>
      <style>{`@keyframes patentSpin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @media (max-width: 700px) {
          .patent-comparison-grid { grid-template-columns: 1fr !important; }
          .patent-comparison-vs { display: none !important; }
          .patent-stepper-label { display: none !important; }
        }
        @media print {
          .patent-no-print { display: none !important; }
        }
      `}</style>

      {/* Hero */}
      <div style={styles.hero} className="patent-no-print">
        <h1 style={styles.heroTitle}>Patent Generator</h1>
        <p style={styles.heroSubtitle}>
          File a provisional patent for ~$200. Our AI drafts the application &mdash; you just describe your invention.
        </p>
        <div style={styles.badgeRow}>
          <span style={styles.badge}><span style={styles.badgeIcon}>$</span> $200 Filing Fee</span>
          <span style={styles.badge}><span style={styles.badgeIcon}>*</span> AI-Drafted</span>
          <span style={styles.badge}><span style={styles.badgeIcon}>#</span> USPTO Format</span>
          <span style={styles.badge}><span style={styles.badgeIcon}>~</span> 24-Hour Turnaround</span>
        </div>
      </div>

      {/* Cost Comparison */}
      {!generated && !generating && (
        <div style={styles.comparisonCard} className="patent-no-print">
          <div style={styles.comparisonTitle}>Why Use the Patent Generator?</div>
          <div className="patent-comparison-grid" style={styles.comparisonGrid}>
            <div style={{ ...styles.comparisonBox, ...styles.comparisonTraditional }}>
              <div style={{ ...styles.comparisonLabel, color: '#ef4444' }}>Traditional Patent Attorney</div>
              <div style={{ ...styles.comparisonPrice, color: '#ef4444' }}>$10,000 - $15,000</div>
              <div style={styles.comparisonDetail}>
                Requires hiring a patent attorney. Extensive back-and-forth drafting process. Takes 3-6 months on average.
              </div>
            </div>
            <div className="patent-comparison-vs" style={styles.vsCircle}>VS</div>
            <div style={{ ...styles.comparisonBox, ...styles.comparisonHub }}>
              <div style={{ ...styles.comparisonLabel, color: '#6366f1' }}>Innovation Hub AI</div>
              <div style={{ ...styles.comparisonPrice, color: '#6366f1' }}>~$200</div>
              <div style={styles.comparisonDetail}>
                AI-generated patent draft in USPTO format. Describe your invention and get a filing-ready document in 24 hours.
              </div>
            </div>
          </div>
          <div style={styles.savingsBanner}>
            Save up to $14,800 on your provisional patent application
          </div>
        </div>
      )}

      {/* Generating Animation */}
      {generating && renderGenerating()}

      {/* Generated Patent Preview */}
      {generated && !generating && renderPatentPreview()}

      {/* Wizard Form */}
      {!generated && !generating && (
        <>
          {/* Stepper */}
          <div style={styles.stepperContainer} className="patent-no-print">
            <div style={styles.stepperRow}>
              {STEP_LABELS.map((label, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <div style={styles.stepConnector} />}
                  <div
                    style={styles.stepItem(currentStep === i, i < currentStep)}
                    onClick={() => { if (i < currentStep) setCurrentStep(i); }}
                  >
                    <div style={styles.stepNumber(currentStep === i, i < currentStep)}>
                      {i < currentStep ? '\u2713' : i + 1}
                    </div>
                    <span className="patent-stepper-label" style={styles.stepLabel(currentStep === i)}>{label}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Form Card */}
          <div style={styles.formCard}>
            <div style={styles.formTitle}>Step {currentStep + 1}: {STEP_LABELS[currentStep]}</div>
            <div style={styles.formSubtitle}>
              {currentStep === 0 && 'Provide the basic details about your invention.'}
              {currentStep === 1 && 'Describe the problem your invention solves and how it differs from existing solutions.'}
              {currentStep === 2 && 'Explain the technical workings, components, and novel elements.'}
              {currentStep === 3 && 'Define the legal claims for your patent. Claims determine the scope of your protection.'}
              {currentStep === 4 && 'Add optional figures and diagrams to illustrate your invention.'}
              {currentStep === 5 && 'Review your application, add inventor details, and generate the patent draft.'}
            </div>
            {renderStepContent()}

            {/* Navigation Buttons (not on step 5, which has its own Generate button) */}
            {currentStep < 5 && (
              <div style={styles.buttonRow}>
                <div>
                  {currentStep > 0 && (
                    <button
                      style={styles.btnSecondary}
                      onClick={() => setCurrentStep(prev => prev - 1)}
                      type="button"
                    >
                      Back
                    </button>
                  )}
                </div>
                <button
                  style={{
                    ...styles.btnPrimary,
                    ...(!canContinue() ? styles.btnDisabled : {}),
                  }}
                  disabled={!canContinue()}
                  onClick={() => setCurrentStep(prev => prev + 1)}
                  type="button"
                >
                  Continue
                </button>
              </div>
            )}
            {currentStep === 5 && (
              <div style={{ ...styles.buttonRow, justifyContent: 'flex-start', marginTop: 16 }}>
                <button
                  style={styles.btnSecondary}
                  onClick={() => setCurrentStep(prev => prev - 1)}
                  type="button"
                >
                  Back
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* My Patent Drafts */}
      <div style={styles.draftsCard} className="patent-no-print">
        <div style={styles.draftsTitle}>My Patent Drafts</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Title</th>
                <th style={styles.th}>Field</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {DEMO_DRAFTS.map((draft) => (
                <tr key={draft.id}>
                  <td style={{ ...styles.td, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {draft.title}
                  </td>
                  <td style={styles.td}>{draft.field}</td>
                  <td style={styles.td}>
                    <span style={styles.statusBadge(draft.status)}>{draft.status}</span>
                  </td>
                  <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{draft.date}</td>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button style={styles.btnSmall} type="button">View</button>
                      <button style={styles.btnSmall} type="button">Edit</button>
                    </div>
                  </td>
                </tr>
              ))}
              {DEMO_DRAFTS.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ ...styles.td, textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>
                    No patent drafts yet. Use the form above to create your first one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
